import {
    Add,
    ArrowDownward,
    ArrowUpward,
    FilterAltOffOutlined,
    FilterAltOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GET, PATCH } from "../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../Utilities/UpdatedSearchableInput";
import BaseTable from "../Table/BaseTable";
import SearchBar from "../Table/Components/SearchBar";

type Filter = {
    search: string;
    district: number | "" | "all";
    village: number | "" | "all";
    hamlet: number | "" | "all";
    powerstation: number | "" | "all";
    status: string;
};

const initialFilter: Filter = {
    search: "",
    district: "",
    village: "",
    hamlet: "",
    powerstation: "",
    status: "active",
};

const initialClear = {
    district: false,
    village: false,
    hamlet: false,
    powerstation: false,
    search: false,
};

const ChangeConnectionType = ({ row, setChangeCount }: any) => {
    const handleLevelChange = async (direction: string) => {
        const res = await PATCH("/connection/change-sequence", {
            id: row.id,
            direction: direction,
        });
        if (res) {
            setChangeCount((c: number) => c + 1);
        }
    };

    return (
        <div>
            <IconButton onClick={() => handleLevelChange("up")} size="small">
                <Tooltip title="Move up">
                    <ArrowUpward fontSize="small" />
                </Tooltip>
            </IconButton>

            <IconButton onClick={() => handleLevelChange("down")} size="small">
                <Tooltip title="Move down">
                    <ArrowDownward fontSize="small" />
                </Tooltip>
            </IconButton>
        </div>
    );
};

const ConnectionTypes = () => {
    const [rowsCount, setRowsCount] = useState(0);
    const [filter, setFilter] = useState<any>(initialFilter);
    const [feedback, setFeedback] = useState("");
    const [clearAll, setClearAll] = useState(initialClear);
    const [changeCount, setChangeCount] = useState(0);
    const [intermediateFilter, setIntermediateFilter] =
        useState<Filter>(initialFilter);
    const [pagination, setPagination] = useState<any>({
        page: 0,
        limit: 100,
    });
    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));

    const getConnectionTypes = () => {
        const regions = Object.fromEntries(
            Object.entries(filter).filter(
                ([k, v]) =>
                    ["district", "village", "hamlet", "powerstation"].includes(
                        k
                    ) &&
                    v &&
                    v !== "all"
            )
        );
        return GET("/connection", {
            page: pagination.page + 1,
            limit: pagination.limit,
            search: filter.search,
            status: filter.status,
            ...regions,
        });
    };

    const { data, isLoading, isError, isSuccess } = useQuery(
        [
            "connection-types",
            pagination.page,
            pagination.limit,
            filter,
            changeCount,
        ],
        getConnectionTypes,
        {
            enabled: Boolean(changeCount),
            onSuccess(res) {
                if (res) setRowsCount(res?.data.count);
            },
        }
    );

    /* ================= CLEAR ALL ================= */

    const handleClearAll = () => {
        // reset UI filters
        setIntermediateFilter(initialFilter);

        // reset applied filters
        setFilter(initialFilter);

        // notify child inputs
        setClearAll({
            district: true,
            village: true,
            hamlet: true,
            powerstation: true,
            search: true,
        });

        // reset search trigger & pagination
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });

        // turn flags off after render
        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    hamlet: false,
                    powerstation: false,
                    search: false,
                }),
            0
        );
    };

    useEffect(() => {
        if (changeCount) {
            setFilter({ ...filter, ...intermediateFilter });
        }
    }, [changeCount]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                // height: mdDown ? "auto" : `calc(100vh - 92px)`,
            }}
        >
            <Card
                elevation={0}
                sx={{
                    bgcolor: theme.palette.common.white,
                    p: 2,
                    mb: "1rem",
                }}
            >
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    xs={12}
                    spacing={1.5}
                >
                    <Grid item xs={12} md={4}>
                        <Typography variant="h5" color="GrayText">
                            Connection Types
                        </Typography>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={6}
                        lg={6}
                        container
                        spacing={1}
                        justifyContent="flex-end"
                    >
                        <Grid item flex={1}></Grid>

                        <Grid item xs={12} md="auto">
                            <MultiPermissionAuthorize
                                ops={["CREATE CONNECTION_TYPE"]}
                            >
                                <Link
                                    to="/connections/types/add"
                                    style={{ textDecoration: "none" }}
                                >
                                    <Button
                                        fullWidth
                                        sx={{ height: "100%" }}
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Add />}
                                    >
                                        add conn. type
                                    </Button>
                                </Link>
                            </MultiPermissionAuthorize>
                        </Grid>
                    </Grid>

                    <Grid item container xs={12} spacing={1.5}>
                        <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                            {" "}
                            <Grid item xs={12} sm={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/region/district"
                                    label="District"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    clearAll={clearAll.district}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/powerstation"
                                    label="Powerstation"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={intermediateFilter.district}
                                    params={{
                                        district: intermediateFilter.district,
                                    }}
                                    clearAll={clearAll.powerstation}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/region/village"
                                    label="Village"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={[
                                        intermediateFilter.district,
                                        intermediateFilter.powerstation,
                                    ]}
                                    params={{
                                        district: intermediateFilter.district,
                                        powerstation:
                                            intermediateFilter.powerstation,
                                    }}
                                    clearAll={clearAll.village}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/region/hamlet"
                                    label="Hamlet"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={[
                                        intermediateFilter.village,
                                        intermediateFilter.powerstation,
                                    ]}
                                    params={{
                                        village: intermediateFilter.village,
                                        powerstation:
                                            intermediateFilter.powerstation,
                                    }}
                                    clearAll={clearAll.hamlet}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <Grid item xs={12} sm={6} lg={3}>
                            <TextField
                                select
                                size="small"
                                fullWidth
                                value={intermediateFilter.status}
                                name="status"
                                label="Status"
                                onChange={(e) =>
                                    setIntermediateFilter({
                                        ...intermediateFilter,
                                        status: e.target.value
                                            ? e.target.value
                                            : "active",
                                    })
                                }
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} lg={3}>
                            <SearchBar
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                clear={clearAll.search}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} lg={3}>
                            <Button
                                color="secondary"
                                variant="outlined"
                                onClick={() => setChangeCount(changeCount + 1)}
                                sx={{ height: "100%" }}
                                startIcon={<FilterAltOutlined />}
                                fullWidth
                            >
                                Search
                            </Button>
                        </Grid>

                        {/* CLEAR ALL */}
                        <Grid item xs={12} sm={6} lg={3}>
                            <Button
                                color="error"
                                variant="outlined"
                                startIcon={<FilterAltOffOutlined />}
                                onClick={handleClearAll}
                                sx={{
                                    height: "100%",
                                    minWidth: theme.spacing(20),
                                }}
                                fullWidth
                            >
                                Clear All
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>
            {(!changeCount || !data?.data.rows.length || isError) && (
                <Alert severity="info">
                    No data to show — please choose a filter.
                </Alert>
            )}
            <Box sx={{ height: "70%" }}>
                <BaseTable
                    headers={{
                        id: "ID",
                        name: "Name",
                        status: "Status",
                        sequence: "Sequence",
                        district: "District",
                        village: "Village",
                        hamlet: "Hamlet",
                        powerstation: "Powerstation",
                    }}
                    data={data?.data.rows.map((row: any) => ({
                        ...row,
                        district: row?.district?.name ?? "Global",
                        village: row?.village?.name ?? "Global",
                        hamlet: row?.hamlet?.name ?? "Global",
                        powerstation: row?.powerstation?.name ?? "Global",
                        status: (
                            <>
                                <Chip
                                    label={row.status.toUpperCase()}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        fontWeight: "bold",
                                        bgcolor:
                                            row.status === "active"
                                                ? green[50]
                                                : red[200],
                                        color:
                                            row.status === "active"
                                                ? green[500]
                                                : red[400],
                                        border: "none",
                                        width: "13ch",
                                    }}
                                />
                            </>
                        ),
                        sequence: (
                            <ChangeConnectionType
                                row={row}
                                setChangeCount={setChangeCount}
                            />
                        ),
                    }))}
                    defaultSelectedHeaders={[
                        "id",
                        "name",
                        "status",
                        "sequence",
                        "regions",
                    ]}
                    reportName="ConnectionTypes__List"
                    reportDetails={{
                        district: filter.district ? filter.district : undefined,
                        powerstation: filter.powerstation
                            ? filter.powerstation
                            : undefined,
                        village: filter.village ? filter.village : undefined,
                        hamlet: filter.hamlet ? filter.hamlet : undefined,
                    }}
                    delEndPoint="/connection"
                    load={Boolean(changeCount)}
                    feedback={feedback}
                    setFeedback={setFeedback}
                    rowsCount={rowsCount}
                    pagination={pagination}
                    setPagination={setPagination}
                    isLoading={isLoading}
                    permissions={{
                        edit: ["UPDATE CONNECTION_TYPE"],
                        delete: ["DELETE CONNECTION_TYPE"],
                    }}
                    queryKey={[
                        "connection-types",
                        pagination.page,
                        pagination.limit,
                        filter,
                    ]}
                />
            </Box>
        </Box>
    );
};

export default ConnectionTypes;
